import { FC, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import IconPayment from '@/assets/icons/icon_payment.svg';
import IconPayout from '@/assets/icons/icon_payout.svg';
import Button from '@/components/elements/Button/BaseButton';
import {
  PAYOUT_BANK_MIN_VALUE,
  PAYOUT_TORETA_MIN_VALUE,
} from '@/config/constants/payout';
import {
  PAYMENT_TYPE_PARAM_KEY,
  PAYOUT_TYPE_PARAM_KEY,
} from '@/config/constants/query';
import { useUserContext } from '@/contexts/user';
import { TransferDeviceType, Wallet } from '@/lib/apollo/types/gen/graphql';
import { getKeirinHelpArticleUrl } from '@/lib/dmm/help';
import { WalletPayoutType } from '@/modelTypes';

import { Headline } from '../style';
import {
  ButtonContainer,
  GuideLink,
  LinkButton,
  Note,
  Section,
  TestSpan,
  Text,
  Title,
  TitleIcon,
  Wrapper,
} from './style';

export const HASH_PAYOUT = 'payout';

interface WalletTemplateProps {
  walletBalance: Wallet['balance'];
  genericCodeWs: number;
  isViewWalletPage: boolean;
}

const WalletTemplate: FC<WalletTemplateProps> = ({
  walletBalance,
  genericCodeWs,
  isViewWalletPage,
}) => {
  const { hash } = useLocation();
  const { user } = useUserContext();
  const ref = useRef<HTMLElement>(null);
  const isPayoutBank = walletBalance >= PAYOUT_BANK_MIN_VALUE;
  const isPayoutToreta = walletBalance >= PAYOUT_TORETA_MIN_VALUE;

  const viewAppealArea = useMemo(() => {
    if (user && user.chargebackAppleal) {
      return (
        user.chargebackAppleal.campaignID.slice(0, 7) == 'gekitok' ||
        user.chargebackAppleal.campaignID.slice(0, 7) == 'choutok'
      );
    }
    return false;
  }, [user]);

  const viewDummyAppealArea = useMemo(() => {
    if (user && user.chargebackAppleal) {
      if (window.innerWidth >= 768) {
        return (
          user.chargebackAppleal.campaignID.slice(0, 7) == 'gekitok' ||
          user.chargebackAppleal.campaignID.slice(0, 7) == 'choutok'
        );
      }
    }
    return false;
  }, [user]);

  const appealMessage = useMemo(() => {
    if (viewAppealArea) {
      if (user.chargebackAppleal?.campaignID.slice(0, 7) == 'gekitok') {
        return '\\ 激得キャンペーン対象 /';
      }
      if (user.chargebackAppleal?.campaignID.slice(0, 7) == 'choutok') {
        return '\\ 超得キャンペーン対象 /';
      }
    }
    return '';
  }, [viewAppealArea]);

  useEffect(() => {
    if (hash === `#${HASH_PAYOUT}` && ref.current) {
      setTimeout(() => {
        window.scrollTo(0, ref.current!.offsetTop);
      }, 0);
    }
  }, [hash, ref]);

  return (
    <>
      <Headline>入金・精算</Headline>
      <Wrapper>
        <Section>
          <Title>
            <TitleIcon icon={IconPayment} />
            入金
          </Title>
          <Text hidden={(genericCodeWs >> 3) % 2 === 1}>
            投票資金の入金を行うことができます。投票資金はいつでもtoreta+へチャージして投票することが可能です。
          </Text>
          <Text hidden={(genericCodeWs >> 3) % 2 === 0}>
            現在入金機能はご利用いただけません。他の決済手段にてtoreta+チャージを行っていただけるようお願いいたします。
          </Text>
          <ButtonContainer>
            {isViewWalletPage ? (
              <LinkButton
                to={`/wallet/payment?${PAYMENT_TYPE_PARAM_KEY}=${TransferDeviceType.Pc}`}
                disabled={(genericCodeWs >> 3) % 2 === 1}
              >
                <Button
                  outline
                  type="button"
                  bgColor="primary"
                  size="m"
                  radius="l"
                  height="56px"
                  fontWeight="bold"
                  disabled={(genericCodeWs >> 3) % 2 === 1}
                >
                  銀行口座
                </Button>
              </LinkButton>
            ) : (
              <LinkButton
                to={`/wallet/payment?${PAYMENT_TYPE_PARAM_KEY}=${TransferDeviceType.Pc}`}
                disabled={true}
              >
                <Button
                  outline
                  type="button"
                  bgColor="primary"
                  size="m"
                  radius="l"
                  height="56px"
                  fontWeight="bold"
                  disabled={true}
                >
                  銀行口座
                </Button>
              </LinkButton>
            )}

            <LinkButton
              to={`/wallet/payment?${PAYMENT_TYPE_PARAM_KEY}=${TransferDeviceType.Atm}`}
              disabled={true}
            >
              <Button
                outline
                type="button"
                bgColor="primary"
                size="m"
                radius="l"
                height="56px"
                fontWeight="bold"
                disabled={true}
              >
                ATM決済
              </Button>
            </LinkButton>
          </ButtonContainer>
          <Text>
            インターネットバンキング決済の利用には、各金融機関の登録（契約）が必要です。
            <br />
            ATM決済の利用はPay-easy（ペイジー）対応のATMからお振込みできます。
            <br />
            {isViewWalletPage ? (
              <>現在、ATM決済のご利用は行えません。</>
            ) : (
              <>ただいま入金はご利用できません。</>
            )}
          </Text>
          <GuideLink
            href={getKeirinHelpArticleUrl(46109)}
            target="_blank"
            rel="noreferrer"
          >
            入金方法について
          </GuideLink>
        </Section>
        <Section ref={ref}>
          <Title>
            <TitleIcon icon={IconPayout} />
            精算
          </Title>
          <Text>
            入金/払戻金 残高を口座振込・toreta+ へチャージすることができます。
          </Text>
          <ButtonContainer>
            <div>
              {viewDummyAppealArea && (
                <TestSpan>
                  <span>{'　'}</span>
                </TestSpan>
              )}
              <LinkButton
                to={`/wallet/payout?${PAYOUT_TYPE_PARAM_KEY}=${WalletPayoutType.Bank}`}
                disabled={!isPayoutBank}
              >
                <Button
                  outline
                  type="button"
                  bgColor="primary"
                  size="m"
                  radius="l"
                  height="56px"
                  fontWeight="bold"
                  disabled={!isPayoutBank}
                >
                  口座振込
                </Button>
              </LinkButton>
              <Note>
                ※<strong>{PAYOUT_BANK_MIN_VALUE.toLocaleString()}円以上</strong>
                から行うことができます。
              </Note>
            </div>
            <div>
              {viewAppealArea ? (
                <TestSpan>
                  <span>{appealMessage}</span>
                </TestSpan>
              ) : (
                <></>
              )}
              <LinkButton
                to={`/wallet/payout?${PAYOUT_TYPE_PARAM_KEY}=${WalletPayoutType.Toreta}`}
                disabled={!isPayoutToreta}
              >
                <Button
                  outline
                  type="button"
                  bgColor="primary"
                  size="m"
                  radius="l"
                  height="56px"
                  fontWeight="bold"
                  disabled={!isPayoutToreta}
                >
                  toreta+
                </Button>
              </LinkButton>
              <Note>
                ※チャージは
                <strong>{PAYOUT_TORETA_MIN_VALUE.toLocaleString()}円</strong>
                から行うことが可能です。
              </Note>
            </div>
          </ButtonContainer>
        </Section>
        <Section>
          <Title>入金、精算履歴の確認</Title>
          <Text>入金・精算の履歴を確認することができます。</Text>
          <ButtonContainer>
            <LinkButton to="/wallet/history">
              <Button
                outline
                type="button"
                bgColor="secondary"
                size="m"
                radius="l"
                height="56px"
                fontWeight="bold"
              >
                入金、精算履歴を確認する
              </Button>
            </LinkButton>
          </ButtonContainer>
        </Section>
      </Wrapper>
    </>
  );
};

export default WalletTemplate;
